// The in-memory / external leaf provider — the simplest possible
// `DatasetProvider` and the framework's first end-to-end proving ground.
//
// It owns a fixed set of named tables (each a declared `Schema` + an
// array of plain-record rows) closed over at construction. It is a pure
// LEAF: it declares schemas, builds a `ProviderSource`, and fetches rows
// into column-major form. It has no `isPushable` / `translate`, so the
// `query` op partitions its predicate to NOTHING-pushed and the whole
// filter (plus any sort / limit / columns) replays client-side through
// the generic residual stage. That is exactly the point — a failure on
// this provider is unambiguously a framework bug, not backend complexity.
//
// `fetch` projects rows to columns via a provider-supplied
// `instancesToColumns`-equivalent (`rowsToColumns` below): one cell per
// declared schema column per row, iterating the schema's column order so
// every column reaches the same row count even when a row omits an
// optional property. The fetched columns therefore size-match the
// declared schema by construction, satisfying the size-strict
// `schemaRefines` post-condition the materialiser asserts; a column whose
// concrete type is not known up front is declared `unknownCol` and
// refined by the fetched values.

import type { Schema } from '../schema/types.ts';
import { type Cell, type Columns, MaterializedDataset } from '../value.ts';
import { KNOWLEDGE_GRAPH_GROUP } from './groups.ts';
import type { DatasetProvider, ProviderSource } from './types.ts';

/**
 * One named in-memory table: its declared output `Schema` and the rows
 * backing it. A row is a plain record keyed by column name; a value
 * absent from the record is projected as `null` so the column still
 * reaches the table's row count (mirroring how CDF omits unset optional
 * properties). Every value must already be a scalar `Cell` — the
 * in-memory provider is a leaf over concrete data, not nested datasets.
 */
export type InMemoryTable = {
  readonly schema: Schema;
  readonly rows: readonly Readonly<Record<string, Cell>>[];
};

/**
 * The provider's absorbed-parameters blob carried on
 * `ProviderSource.native`. The in-memory provider pushes nothing down, so
 * the only thing it needs at fetch time is which table to read — the
 * dataset `name`. JSON-stable (a bare string field), so it keys the
 * source cache cleanly.
 */
export type InMemoryNative = {
  readonly name: string;
};

/**
 * The params the in-memory provider's `schema` / `buildSource` consume:
 * the dataset name the `query` op resolved this provider for. The
 * absorbed `native` is derived from `name` alone (nothing is pushed), so
 * no separate native field is threaded in.
 */
export type InMemoryParams = {
  readonly name: string;
};

/**
 * Project an in-memory table's rows into column-major `Columns` matching
 * its declared `Schema` — the in-memory counterpart of CDF's
 * `instancesToColumns`. Iterates the schema's column order so every
 * column reaches the same row count; a value absent from a row record
 * collapses to `null`.
 */
const rowsToColumns = (rows: InMemoryTable['rows'], schema: Schema): Columns => {
  const names = [...schema.keys()];
  return Object.fromEntries(names.map((name) => [name, rows.map((row) => row[name] ?? null)]));
};

/**
 * Build an in-memory / external leaf provider over a fixed set of named
 * tables. `id` is the dispatch + cache discriminator (the
 * `ProviderSource.providerId`); `group` defaults to the knowledge-graph
 * group so a `query` op resolves it. The tables are captured by reference
 * — the caller owns their lifetime.
 *
 * The provider is a leaf only: it implements `claims` / `schema` /
 * `buildSource` / `fetch` and deliberately omits `isPushable` /
 * `translate` / `rewrite`, so every constraint replays client-side.
 */
export const createInMemoryProvider = (
  id: string,
  tables: Readonly<Record<string, InMemoryTable>>,
  group: string = KNOWLEDGE_GRAPH_GROUP,
): DatasetProvider<InMemoryParams, InMemoryNative> => {
  const tableFor = (name: string): InMemoryTable | undefined => tables[name];
  return {
    id,
    group,
    claims: (name) => tableFor(name) !== undefined,
    schema: (name) => {
      const table = tableFor(name);
      if (table === undefined) {
        throw new Error(`in-memory provider '${id}' was asked for schema of an unclaimed table '${name}'`);
      }
      return table.schema;
    },
    buildSource: ({ name }) => {
      const table = tableFor(name);
      if (table === undefined) {
        throw new Error(`in-memory provider '${id}' was asked to build a source for an unclaimed table '${name}'`);
      }
      const source: ProviderSource = { kind: 'provider', providerId: id, native: { name } satisfies InMemoryNative };
      return { source, schema: table.schema };
    },
    fetch: async (source): Promise<MaterializedDataset> => {
      const native = source.native as InMemoryNative | undefined;
      const table = native === undefined ? undefined : tableFor(native.name);
      if (table === undefined) {
        throw new Error(
          `in-memory provider '${id}' cannot fetch source for table '${native?.name ?? '(missing)'}' — not registered`,
        );
      }
      // In-memory tables are not instance-backed, so the identity axis is
      // `undefined`.
      return new MaterializedDataset(rowsToColumns(table.rows, table.schema), table.schema, undefined);
    },
  };
};
